/**
 * Every legacy web GOT report (docs/designs/got-legacy-map.md §7 and Appendix D), with its exact
 * filters, required markers, output headings and columns. One definition drives the filter screen,
 * the output and the print preview (reports-page.tsx).
 *
 * Corrected legacy typos: "Summery" → "Summary", "Pariod" → "Period", "Female Type $" → "Female Type %",
 * "Obsevations" → "Observations". The QCB "GOT Report" printed numeric Crop / Variety ids and
 * YYYY-MM-DD dates (legacy bugs); names and DD-MM-YYYY are shown.
 */
import { GOT_REPORT_WINDOWS } from "@nexseed/shared/got";

export type GotReportFilter =
  | { kind: "period"; label: "Period" | "Arrival Period" }
  | { kind: "cropVariety"; blank: "ALL" | "Select" }
  | { kind: "resultOn" }
  | { kind: "stage" }
  | { kind: "result" }
  | { kind: "age" }
  | { kind: "person" };

export type CellKind =
  | "text"
  | "date"
  | "lot"
  | "num"
  | "pct2"
  | "pct3"
  /** One figure of the row's `sloc` summary ("path | bags | qty" per line), a line per SLOC. */
  | "slocBin"
  | "slocNob"
  | "slocQty";
export interface ReportCol {
  key: string;
  header: string;
  kind?: CellKind | undefined;
}
export interface ReportGroup {
  group: string;
  key: string;
  columns: ReportCol[];
}

export interface GotReportDef {
  key: string;
  /** The legacy menu label. */
  menu: string;
  /** The screen title strip. */
  title: string;
  /** The filter box's caption. */
  box?: string | undefined;
  /** Legacy roles whose Reports menu lists it. */
  roles: ("Quality" | "QCS" | "QCM" | "QualityGOT" | "QCB")[];
  /** null: no filter screen — the report opens "As on Date" today (QCB summaries). */
  filters: GotReportFilter[] | null;
  columns: (ReportCol | ReportGroup)[];
  /** Report – Final Grade has [Back] only (p246). */
  backOnly?: boolean | undefined;
  /** QCB summaries: No. of Lots opens this detail. */
  drill?:
    | {
        title: string;
        heading: string;
        window?: string | undefined;
        columns: (ReportCol | ReportGroup)[];
      }
    | undefined;
  /** Menu-only legacy reports: what is not known. */
  stub?: string | undefined;
  manualPages: string;
}

const c = (key: string, header: string, kind?: CellKind): ReportCol => ({
  key,
  header,
  kind,
});
const g = (key: string, group: string, columns: ReportCol[]): ReportGroup => ({
  key,
  group,
  columns,
});

/** "SLOC" over Bin | NoB | Qty, each read from the row's one `sloc` summary (user, 2026-10-31). */
const SLOC = g("sloc", "SLOC", [
  c("sloc:bin", "Bin", "slocBin"),
  c("sloc:nob", "NoB", "slocNob"),
  c("sloc:qty", "Qty", "slocQty"),
]);

/** F and M under one "SP Code" heading, as the Unloading Sheet heads them (user, 2026-10-26). */
const SP_CODE = g("spCode", "SP Code", [c("spFemale", "F"), c("spMale", "M")]);
const SOWING_GROUP = g("sowing", "IN_TERRA: Sowing", [
  c("replNo", "Replication No."),
  c("dos", "Date of Sowing", "date"),
  c("sowingPlot", "Sowing Plot"),
  c("seeds", "No. of Seeds", "num"),
]);
const ARRIVAL_LEAD = [
  c("doa", "Date of Arrival", "date"),
  c("crop", "Crop"),
  c("variety", "Variety"),
  c("lotNo", "Lot No.", "lot"),
  c("qty", "Qty", "num"),
];
const PERIOD_CV: GotReportFilter[] = [
  { kind: "period", label: "Arrival Period" },
  { kind: "cropVariety", blank: "ALL" },
];
const SUMMARY = [
  c("crop", "Crop"),
  c("variety", "Variety"),
  c("lots", "No. of Lots", "num"),
];

export const GOT_REPORTS: GotReportDef[] = [
  {
    key: "sample-dispatch",
    menu: "GOT Sample Dispatch Report",
    title: "GOT Sample Dispatch Report",
    box: "GOT Sample Dispatch Report",
    roles: ["Quality", "QCM", "QualityGOT"],
    filters: [
      { kind: "period", label: "Period" },
      { kind: "cropVariety", blank: "ALL" },
    ],
    columns: [
      c("doa", "Date of Arrival", "date"),
      c("crop", "Crop"),
      c("variety", "Variety"),
      c("lotNo", "Lot No.", "lot"),
      c("sampleNo", "Sample No.", "lot"),
      c("dosr", "DOSR", "date"),
      c("dosc", "DOSC", "date"),
      c("dosd", "DOSD", "date"),
      c("location", "Location"),
      c("mode", "Mode"),
      c("gsdnNo", "GSDN No.", "lot"),
    ],
    manualPages: "p135–136, p229, p264",
  },
  {
    key: "pending",
    menu: "Pending GOT Report",
    title: "Report - Pending GOT Report",
    box: "Pending GOT Report",
    roles: ["QualityGOT"],
    filters: [{ kind: "cropVariety", blank: "ALL" }],
    columns: [
      c("doa", "Date of Arrival", "date"),
      c("crop", "Crop"),
      c("variety", "Variety"),
      c("lotNo", "Lot No.", "lot"),
      c("nob", "NoB", "num"),
      c("qty", "Qty", "num"),
      c("dosr", "DOSR", "date"),
      c("dosc", "DOSC", "date"),
      c("dosd", "DOSD", "date"),
      c("gotStatus", "GOT Status"),
      c("location", "Location"),
      c("mode", "Mode"),
    ],
    manualPages: "p260, screenshot 154645",
  },
  {
    key: "daily-result",
    menu: "Daily GOT Result Report",
    title: "Report - Daily GOT Result Report",
    box: "Daily GOT Result Report",
    roles: ["QualityGOT", "QCM"],
    filters: [{ kind: "resultOn" }, { kind: "stage" }],
    columns: [
      c("doa", "Date of Arrival", "date"),
      c("crop", "Crop"),
      c("variety", "Variety"),
      c("lotNo", "Lot No.", "lot"),
      c("nob", "NoB", "num"),
      c("qty", "Qty", "num"),
      c("pp", "PP"),
      c("moist", "Moist %", "num"),
      c("germ", "Germ %", "num"),
      c("dot", "DOT", "date"),
      c("qcStatus", "QC Status"),
      c("dogr", "DOGR", "date"),
      c("gotStatus", "GOT Status"),
    ],
    manualPages: "p233–234, p261–262",
  },
  {
    key: "result",
    menu: "GOT Result Report",
    title: "Report - Periodical GOT Report",
    box: "Periodical GOT Report",
    roles: ["QualityGOT"],
    filters: [
      { kind: "period", label: "Period" },
      { kind: "cropVariety", blank: "Select" },
      { kind: "result" },
    ],
    columns: [
      c("doa", "Date of Arrival", "date"),
      c("crop", "Crop"),
      c("variety", "Variety"),
      c("lotNo", "Lot No.", "lot"),
      c("nob", "NoB", "num"),
      c("qty", "Qty", "num"),
      c("stage", "Stage"),
      c("pp", "PP"),
      c("moist", "Moist %", "num"),
      c("germ", "Germ %", "num"),
      c("dot", "DOT", "date"),
      c("gotResult", "GOT Result"),
      c("gp", "Genetic Purity %", "pct3"),
    ],
    manualPages: "p263",
  },
  {
    key: "periodical-result-qcs",
    menu: "Periodical GOT Result Report (QCS)",
    title: "Report - Periodical GOT Result Report",
    box: "Periodical GOT Result Report",
    roles: ["QCS"],
    filters: [{ kind: "period", label: "Period" }],
    columns: [
      c("doa", "Date of Arrival", "date"),
      c("crop", "Crop"),
      c("variety", "Variety"),
      c("lotNo", "Lot No.", "lot"),
      c("nob", "NoB", "num"),
      c("qty", "Qty", "num"),
      c("stage", "Stage"),
      c("pp", "PP"),
      c("moist", "Moist %", "num"),
      c("germ", "Germ %", "num"),
      c("dot", "DOT", "date"),
      c("dogr", "DOGR", "date"),
      c("gotStatus", "GOT Status"),
      c("gp", "GP %", "pct2"),
    ],
    manualPages: "p184–185",
  },
  {
    key: "periodical-result-qcm",
    menu: "Periodical GOT Result Report (QCM)",
    title: "Report - Periodical GOT Result Report",
    box: "Periodical GOT Result Report",
    roles: ["QCM"],
    filters: [{ kind: "period", label: "Period" }],
    columns: [
      c("doa", "Date of Arrival", "date"),
      c("crop", "Crop"),
      c("variety", "Variety"),
      c("lotNo", "Lot No.", "lot"),
      SP_CODE,
      c("qty", "Qty", "num"),
      c("gotStatus", "GOT Status"),
      c("dogt", "DoGT", "date"),
    ],
    manualPages: "p242–243",
  },
  {
    key: "sowing-pending",
    menu: "GOT Sowing Pending Report",
    title: "Report - GOT Sowing Pending Report",
    box: "GOT Sowing Pending Report",
    roles: ["QCM"],
    filters: PERIOD_CV,
    columns: [
      ...ARRIVAL_LEAD,
      c("gotStatus", "GOT Status"),
      c("dosc", "DOSC", "date"),
      c("dosd", "DOSD", "date"),
    ],
    manualPages: "p239–240",
  },
  {
    key: "transplantation-pending",
    menu: "Lot Transplantation Pending Report",
    title: "Report - Pending GOT Report - Lot Transplantation Pending",
    box: "Lot Transplantation Pending",
    roles: ["QCM"],
    filters: PERIOD_CV,
    columns: [...ARRIVAL_LEAD, SOWING_GROUP],
    manualPages: "p240–241",
  },
  {
    key: "data-pending",
    menu: "GOT Data Pending Report",
    title: "Report - Pending GOT Report - GOT Data Pending",
    box: "GOT Data Pending",
    roles: ["QCM"],
    filters: [...PERIOD_CV, { kind: "age" }],
    columns: [
      ...ARRIVAL_LEAD,
      SOWING_GROUP,
      g("tp", "IN_TERRA: Transplanting", [
        c("dot", "Date of Transplant", "date"),
        c("tpPlot", "Transplant Plot"),
        c("range", "Range"),
        c("rows", "No of Rows"),
        c("bedNo", "Bed No."),
        c("direction", "Direction"),
        c("state", "State"),
        c("location", "Location"),
        c("plants", "No. of Plants", "num"),
      ]),
    ],
    manualPages: "p241–242",
  },
  {
    key: "prod-person-status",
    menu: "Prod Per Wise GOT Status Report",
    title: "Report - Production Person Wise GOT Status Report",
    box: "Production Person Wise GOT Status Report",
    roles: ["QCM"],
    filters: [{ kind: "period", label: "Arrival Period" }, { kind: "person" }],
    columns: [
      c("doa", "Date of Arrival", "date"),
      c("crop", "Crop"),
      c("variety", "Variety"),
      c("lotNo", "Lot No.", "lot"),
      SP_CODE,
      c("nob", "NoB", "num"),
      c("qty", "Qty", "num"),
      c("prodLocation", "Production Location"),
      c("organiser", "Organiser"),
      c("farmer", "Farmer"),
      c("prodPersonnel", "Production Personnel"),
      c("gotStatus", "GOT Status"),
      c("dogt", "DoGT", "date"),
      c("gp", "GP %", "pct2"),
    ],
    manualPages: "p243–244",
  },
  {
    key: "final-grade",
    menu: "GOT Final Grade Report",
    title: "Report - GOT Final Grade Report",
    box: "GOT Final Grade Report",
    roles: ["QCM"],
    filters: [{ kind: "period", label: "Period" }],
    backOnly: true,
    columns: [
      c("crop", "Crop"),
      c("variety", "Variety"),
      c("lotNo", "Lot Number", "lot"),
      c("qty", "Qty", "num"),
      c("doa", "Arrival Date", "date"),
      c("resultDate", "Result Date", "date"),
      c("ampSamples", "No of Amp. Samples", "num"),
      c("femaleType", "F", "num"),
      c("femaleTypePct", "F %", "pct3"),
      c("maleType", "M", "num"),
      c("maleTypePct", "M %", "pct3"),
      c("offType", "OTF", "num"),
      c("offTypePct", "OTF %", "pct3"),
      c("otfDetails", "OTF Details"),
      c("impuritiesPct", "Impurities %", "pct3"),
      c("gp", "GP %", "pct3"),
      c("btsRemark", "BTS Remarks"),
      c("btsGrade", "BTS Grade"),
      c("prodGrade", "Prod. Grade"),
      c("finalGrade", "Final GOT Grade"),
    ],
    manualPages: "p245–246",
  },
  {
    key: "data-in-terra",
    menu: "GOT Data Report (In Terra)",
    title: "Report - GOT Data Report (In Terra)",
    roles: ["QCM"],
    filters: null,
    columns: [],
    stub: "GOT Data Report (In Terra) is in the QC Manager's Reports menu (p213) but the manual has no screenshot of it: its filters, headings and columns are unknown.",
    manualPages: "p213 (menu only)",
  },
  {
    key: "data-in-situ",
    menu: "GOT Data Report (In SITU)",
    title: "Report - GOT Data Report (In SITU)",
    roles: ["QCM"],
    filters: null,
    columns: [],
    stub: "GOT Data Report (In SITU) is in the QC Manager's Reports menu (p213) but the manual has no screenshot of it: its filters, headings and columns are unknown.",
    manualPages: "p213 (menu only)",
  },
  // ---- QC Biotech (QCB): no filter screen, "As on Date" today, summary → detail
  {
    key: "qcb-sowing-pending",
    menu: "GOT Sowing Pending Report",
    title: "Report - GOT Sowing Pending Summary Report",
    roles: ["QCB"],
    filters: null,
    columns: SUMMARY,
    drill: {
      title: "Report - GOT Sowing Pending Detailed Report",
      heading: "GOT Sowing Pending Detailed Report",
      columns: [
        c("doa", "Date Of Arrival", "date"),
        c("crop", "Crop"),
        c("variety", "Variety"),
        c("lotNo", "Lot No", "lot"),
        c("sampleNo", "Sample", "lot"),
        SP_CODE,
        c("nob", "NoB", "num"),
        c("qty", "Qty", "num"),
        c("gotStatus", "GOT Status"),
        c("prodLocation", "PD Loc"),
        c("organiser", "Organiser"),
        c("farmer", "Farmer"),
        c("prodPersonnel", "PP"),
        SLOC,
      ],
    },
    manualPages:
      "p273 (detail columns from the QC-App's Sowing Pending report)",
  },
  {
    key: "qcb-transplant-pending",
    menu: "GOT Transplant Pending Report",
    title: "Report - GOT Transplanting Pending Summary Report",
    roles: ["QCB"],
    filters: null,
    columns: SUMMARY,
    drill: {
      title: "Report - GOT Transplanting Pending Detailed Report",
      heading: "GOT Transplanting Pending Detailed Report",
      window: GOT_REPORT_WINDOWS.tppending,
      columns: [
        c("doa", "Arrival Date", "date"),
        c("crop", "Crop"),
        c("variety", "Variety"),
        c("lotNo", "Lot No.", "lot"),
        c("sampleNo", "Sample No.", "lot"),
        SP_CODE,
        c("dos", "Date of sowing (DOS)", "date"),
        c("nurseryLocation", "Nursery location"),
        c("bedNo", "Bed no"),
        c("treyNo", "Trey no."),
        c("treys", "No of trey", "num"),
      ],
    },
    manualPages: "p274–275",
  },
  {
    key: "qcb-observation-pending",
    menu: "GOT Observation Pending Report",
    title: "Report - GOT Observation Pending Summary Report",
    roles: ["QCB"],
    filters: null,
    columns: SUMMARY,
    drill: {
      title: "Report - GOT Observation Pending Detailed Report",
      heading: "GOT Observation Pending Detailed Report",
      window: GOT_REPORT_WINDOWS.fmvpending,
      columns: [
        c("doa", "Date of Arrival", "date"),
        c("crop", "Crop"),
        c("variety", "Variety"),
        c("lotNo", "Lot No.", "lot"),
        c("sampleNo", "Sample No.", "lot"),
        SP_CODE,
        c("tl", "TL"),
        c("dot", "DOT", "date"),
        c("bedNo", "Bed No"),
        c("direction", "Direction"),
      ],
    },
    manualPages:
      "p275 (legacy screenshot titled Transplanting by mistake; detail columns from the QC-App)",
  },
  {
    key: "qcb-biotech",
    menu: "GOT Biotech Test Initiative Report",
    title: "Report - GOT Biotech Test Initiative Summary Report",
    roles: ["QCB"],
    filters: null,
    columns: SUMMARY,
    drill: {
      title: "Report - GOT Biotech Test Initiative Detailed Report",
      heading: "GOT Biotech Test Initiative Detailed Report",
      window: GOT_REPORT_WINDOWS.btspending,
      columns: [
        c("crop", "Crop"),
        c("variety", "Variety"),
        c("lotNo", "Lot No.", "lot"),
        c("sampleNo", "Sample No.", "lot"),
        SP_CODE,
        c("doa", "Arrival Date", "date"),
        c("dos", "Date of sowing (DOS)", "date"),
        c("trd", "Tentative result date (TRD)", "date"),
      ],
    },
    manualPages: "p276–277",
  },
  {
    key: "qcb-periodical",
    menu: "GOT Report",
    title: "Report - Periodical GOT Report",
    box: "Periodical GOT Report",
    roles: ["QCB"],
    filters: [
      { kind: "period", label: "Period" },
      { kind: "cropVariety", blank: "Select" },
    ],
    columns: [
      c("doa", "Date of Arrival", "date"),
      c("crop", "Crop"),
      c("variety", "Variety"),
      c("lotNo", "Lot No.", "lot"),
      c("sampleNo", "Sample No.", "lot"),
      c("resultDate", "Result Date", "date"),
      c("ppn", "PPN", "num"),
      g("female", "F", [
        c("female", "No.", "num"),
        c("femalePct", "%", "pct2"),
      ]),
      g("male", "M", [c("male", "No.", "num"), c("malePct", "%", "pct2")]),
      g("oof", "OTF", [c("oof", "No.", "num"), c("oofPct", "%", "pct2")]),
      c("otfDetails", "OTF Details"),
      c("gp", "GP (%)", "pct2"),
      c("resultFlag", "Result Flag"),
      c("result", "Result"),
    ],
    manualPages: "p277–278",
  },
  {
    key: "qcb-data",
    menu: "GOT Data Report",
    title: "Report - GOT Data Report",
    roles: ["QCB"],
    filters: null,
    columns: [],
    stub: 'The QC Biotech "GOT Data Report" is in the Reports menu (p271) but has no screenshot: its filters, headings and columns are unknown.',
    manualPages: "p271 (menu only)",
  },
];

export const gotReport = (key: string) =>
  GOT_REPORTS.find((r) => r.key === key);

export const ROLE_LABEL: Record<GotReportDef["roles"][number], string> = {
  Quality: "Quality (QC)",
  QCS: "QC Supervisor (QCS)",
  QCM: "QC Manager (QCM)",
  QualityGOT: "Quality GOT",
  QCB: "QC Biotech (QCB)",
};
